import { useDispatch } from "react-redux";
import { Box, Button, Typography } from "@mui/material";
import React, { JSX, useEffect, useState } from "react";

import QrPanelLayout from "../QrPanelLayout";
import PaymentStatusChip from "./PaymentStatusChip";
import WebSocketStatusChip from "./WebSocketStatusChip";
import Overlay from "../../../../../components/Overlay/Overlay";
import useNepalPayWSS from "../../../../../hooks/useNepalPayWSS";
import {
  NepalPayQrPaymentStatus,
  NepalPayQrPaymentStatusSuccess,
  TQrPaymentInfo
} from "../../../../../interfaces/QrPayment";
import { notificationAdd } from "../../../../../actions/notification";
import { BillDocumentType } from "../../../../../interfaces/BillInterfaces";
import useCurrentResourceCentre from "../../../../../hooks/useCurrentResourceCentre";
import { checkNepalPayQrPaymentStatus, getNepalPayDynamicQrString } from "../../../../../api/bill";
import { useCurrentStore } from "../../../../../hooks/useCurrentStore";
import { useCurrentCounter } from "../../../../../hooks/useCurrentCounter";
import { QrPaymentMethodsEnum } from "../../../../../enum/billing";
import Qr from "../../../../../components/Qr/Qr";

interface QrPaymentPanelProps {
  draft: BillDocumentType;
  currentBillCounter: string;
  selectedPaymentMethod: keyof TQrPaymentInfo | null;
  onClose: () => void;
}

const isSuccessStatusResponse = (
  data: NepalPayQrPaymentStatus | NepalPayQrPaymentStatusSuccess
): data is NepalPayQrPaymentStatusSuccess => "responseStatus" in data;

const isDescriptionResponse = (
  data: NepalPayQrPaymentStatus | NepalPayQrPaymentStatusSuccess
): data is NepalPayQrPaymentStatus => "responseDescription" in data;

export default function NepalPayQrPanel({
  draft,
  onClose,
  currentBillCounter,
  selectedPaymentMethod
}: QrPaymentPanelProps): JSX.Element {
  const dispatch = useDispatch();
  const rc = useCurrentResourceCentre();
  const { currentStore: storeLabel } = useCurrentStore();
  const { currentCounter: terminalLabel } = useCurrentCounter();

  const [qrString, setQrString] = useState("");
  const [validationTraceId, setValidationTraceId] = useState("");

  const paidAmount =
    draft?.paymentInfo?.paymentBreakdown.find(
      (b) => b.method.toLowerCase() === QrPaymentMethodsEnum.NEPALPAY.toLowerCase()
    )?.amount ?? 0;

  const npay = useNepalPayWSS({
    validationTraceId,
    onStatusUpdate: (status) => {
      if (status === "COMPLETED") {
        dispatch(
          notificationAdd({
            id: new Date().getMilliseconds(),
            variant: "success",
            message: "Payment received!",
            autoTimeout: true
          })
        );
      }
    }
  });

  // If hook returned null (no apiKey/publicKey available)
  const paymentStatus = npay?.paymentStatus ?? null;
  const wsStatus = npay?.connectionStatus ?? "DISCONNECTED";
  const transactionId = npay?.transactionId ?? null;

  const handlePaymentCheck = async () => {
    try {
      if (
        !validationTraceId ||
        !rc?.dynamicQrPaymentInfo?.nepalPay?.acquirerId ||
        !rc?.dynamicQrPaymentInfo?.nepalPay?.merchantId ||
        !transactionId
      ) {
        dispatch(
          notificationAdd({
            id: new Date().getTime(),
            variant: "error",
            message: "Payment validation trace id, transaction id or acquirer id not found.",
            autoTimeout: true
          })
        );
        return;
      }

      const payload = {
        validationTraceId,
        merchantId: rc?.dynamicQrPaymentInfo?.nepalPay?.merchantId,
        acquirerId: rc?.dynamicQrPaymentInfo?.nepalPay?.acquirerId,
        nqrTxnId: transactionId,
        rcId: rc?.id
      };

      const { data, success, message } = await checkNepalPayQrPaymentStatus(payload);

      if (
        success &&
        isSuccessStatusResponse(data) &&
        data.responseStatus === "SUCCESS" &&
        data.responseCode === "200"
      ) {
        dispatch(
          notificationAdd({
            id: new Date().getTime(),
            variant: "success",
            message: "Payment completed",
            autoTimeout: true
          })
        );

        return;
      }

      if (success && isDescriptionResponse(data) && data.responseDescription) {
        dispatch(
          notificationAdd({
            id: new Date().getTime(),
            variant: "success",
            message: data.responseDescription || message,
            autoTimeout: true
          })
        );

        return;
      }
    } catch (err) {
      dispatch(
        notificationAdd({
          id: new Date().getTime(),
          variant: "error",
          message: (err as Error).message || "Failed to check payment status.",
          autoTimeout: true
        })
      );
    }
  };

  // effects
  useEffect(() => {
    (async () => {
      try {
        if (!storeLabel || !terminalLabel) {
          dispatch(
            notificationAdd({
              id: new Date().getTime(),
              variant: "error",
              message: "Store or counter is not available",
              autoTimeout: true
            })
          );
          return;
        }

        if (rc?.id) {
          const { success, data } = await getNepalPayDynamicQrString({
            resourceCentreId: rc.id,
            amount: paidAmount,
            billNumber: String(draft.billNumber),
            storeLabel,
            terminalLabel
          });

          if (success) {
            setQrString(data.data.qrString);
            setValidationTraceId(data.data.validationTraceId);
          }
        }
      } catch (error) {
        dispatch(
          notificationAdd({
            id: new Date().getTime(),
            variant: "error",
            message:
              (error as Error).message ||
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (error as any)?.data?.message ||
              "Unexpected error occurred",
            autoTimeout: true
          })
        );
      }
    })();
  }, [dispatch, draft.billNumber, paidAmount, rc?.id, storeLabel, terminalLabel]);

  if (!npay) {
    return (
      <QrPanelLayout
        title="Pay with Nepal Pay"
        draft={draft}
        onClose={onClose}
        currentBillCounter={currentBillCounter}
        selectedPaymentMethod={selectedPaymentMethod}
      >
        <Typography color="error">
          NepalPay QR configuration is missing. Please check your resource centre setup.
        </Typography>
      </QrPanelLayout>
    );
  }

  return (
    <QrPanelLayout
      title="Pay with Nepal Pay"
      draft={draft}
      onClose={onClose}
      currentBillCounter={currentBillCounter}
      selectedPaymentMethod={selectedPaymentMethod}
    >
      <Overlay
        text={paymentStatus === "COMPLETED" ? "Payment completed!" : "Processing..."}
        show={paymentStatus !== null && !["ENTR", "FAILED"].includes(paymentStatus)}
      >
        {qrString && <Qr value={qrString} />}
      </Overlay>

      <Box
        sx={{
          mt: 4,
          display: "flex",
          flexDirection: "column",
          gap: 1
        }}
      >
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Typography>Payment Status:</Typography>
          <PaymentStatusChip status={paymentStatus} />
        </Box>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Typography>Connection Status:</Typography>
          <WebSocketStatusChip status={wsStatus} />
        </Box>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Typography>Check Payment Status:</Typography>

          <Button variant="contained" size="small" onClick={handlePaymentCheck}>
            Check
          </Button>
        </Box>
      </Box>
    </QrPanelLayout>
  );
}
